/**
 * HaurakiLogo — the brand mark for Hauraki.
 *
 * The "seven-layer acceleration" mark: seven nested rounded squares spiralling
 * anticlockwise (−2° → −78°) and gaining opacity as they tighten inward to a
 * solid core with a single white spark. The layers reading as motion/momentum —
 * an accelerating stack settling on a point. This is the canonical Hauraki mark,
 * shared with hauraki.us; the brand blue (#0A84FF) lives ONLY inside this SVG so
 * adopting the mark here doesn't move any platform design token.
 *
 * Self-contained: one inline SVG using currentColor + per-layer opacity (no
 * gradient IDs to collide), so many logos on a page never conflict. Scales
 * cleanly from a 16px favicon to a login hero. The optional wordmark uses the
 * `.wordmark` class, which inherits the app's sans stack.
 */
import styles from './HaurakiLogo.module.css';

/** Canonical Hauraki brand blue. Scoped to the mark; not a design token. */
const HAURAKI_BLUE = '#0A84FF';

type Props = {
  /** Mark height in px (wordmark scales with it). Default 32. */
  size?: number;
  /** Render the "Hauraki" wordmark beside the mark. */
  showWordmark?: boolean;
  /** Wordmark text. Default "Hauraki". */
  wordmark?: string;
  /** Override the mark colour (defaults to the Hauraki brand blue). */
  color?: string;
  className?: string;
  /** Accessible label; defaults to the wordmark or "Hauraki". */
  title?: string;
};

export function HaurakiLogo({
  size = 32,
  showWordmark = false,
  wordmark = 'Hauraki',
  color = HAURAKI_BLUE,
  className,
  title,
}: Props) {
  const label = title ?? (showWordmark ? wordmark : 'Hauraki');

  return (
    <span
      className={[styles.root, className].filter(Boolean).join(' ')}
      role="img"
      aria-label={label}
    >
      <svg
        className={styles.mark}
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        aria-hidden="true"
      >
        {/* Seven-layer acceleration: nested rounded squares spiralling inward. */}
        <g color={color}>
          <rect x="3" y="3" width="58" height="58" rx="13" fill="currentColor" opacity="0.08" transform="rotate(-2 32 32)" />
          <rect x="7.5" y="7.5" width="49" height="49" rx="11" fill="currentColor" opacity="0.14" transform="rotate(-7 32 32)" />
          <rect x="12" y="12" width="40" height="40" rx="9" fill="currentColor" opacity="0.23" transform="rotate(-15 32 32)" />
          <rect x="16.5" y="16.5" width="31" height="31" rx="7" fill="currentColor" opacity="0.36" transform="rotate(-27 32 32)" />
          <rect x="20" y="20" width="24" height="24" rx="5.5" fill="currentColor" opacity="0.56" transform="rotate(-42 32 32)" />
          <rect x="23.5" y="23.5" width="17" height="17" rx="4" fill="currentColor" opacity="0.74" transform="rotate(-60 32 32)" />
          <g transform="rotate(-78 32 32)">
            <rect x="25.5" y="25.5" width="13" height="13" rx="3.25" fill="currentColor" />
            <circle cx="32" cy="32" r="2.35" fill="#FFFFFF" />
          </g>
        </g>
      </svg>

      {showWordmark && (
        <span className={styles.wordmark} style={{ fontSize: size * 0.72 }}>
          {wordmark}
        </span>
      )}
    </span>
  );
}

export default HaurakiLogo;
